
import { supabase } from '@/integrations/supabase/client';
import { formatPhoneToE164 } from './courierBase';

/**
 * Проверка существования телефона
 * @param phone - Номер телефона
 * @returns Результат проверки
 */
export const checkPhoneExists = async (phone: string) => {
  // Нормализация номера телефона для поиска
  const normalizedPhone = formatPhoneToE164(phone);
  
  // Удаляем + для совместимости с записями без +
  const phoneWithoutPlus = normalizedPhone.startsWith('+') 
    ? normalizedPhone.substring(1) 
    : normalizedPhone;
  
  console.log('Проверка телефона. Оригинальный:', phone);
  console.log('Нормализованный с +:', normalizedPhone);
  console.log('Нормализованный без +:', phoneWithoutPlus);
  
  // Тестовый номер для отладки
  if (phone === '+71234567890' || phone === '71234567890') {
    console.log('Найден тестовый номер телефона');
    return { exists: true, error: null };
  }

  try {
    // Проверяем с + и без +, избегая оператора or
    const { data: phoneData, error: phoneError } = await supabase
      .from('couriers')
      .select('id')
      .eq('phone', normalizedPhone)
      .limit(1);
      
    // Если первая попытка не удалась, пробуем вторую с телефоном без +
    if (phoneError || !phoneData || phoneData.length === 0) {
      const { data: phoneWithoutPlusData, error: phoneWithoutPlusError } = await supabase
        .from('couriers')
        .select('id')
        .eq('phone', phoneWithoutPlus)
        .limit(1);
        
      const exists = phoneWithoutPlusData && phoneWithoutPlusData.length > 0;
      console.log('Результат проверки телефона без +:', phone, exists);
      
      // Выводим ID курьера, если нашли
      if (exists && phoneWithoutPlusData && phoneWithoutPlusData.length > 0) {
        console.log('ПРОВЕРКА: НАЙДЕН ID КУРЬЕРА В ТАБЛИЦЕ COURIERS:', phoneWithoutPlusData[0].id);
      }
      
      return { exists, error: null };
    } else {
      const exists = phoneData && phoneData.length > 0;
      console.log('Результат проверки телефона с +:', phone, exists);
      
      // Выводим ID курьера, если нашли
      if (exists && phoneData && phoneData.length > 0) {
        console.log('ПРОВЕРКА: НАЙДЕН ID КУРЬЕРА В ТАБЛИЦЕ COURIERS:', phoneData[0].id);
      }
      
      return { exists, error: null };
    }
  } catch (error: any) {
    console.error('Ошибка проверки телефона:', error);
    return { exists: false, error };
  }
};

/**
 * Отправка OTP на телефон
 * @param phone - Номер телефона
 * @returns Результат операции
 */
export const sendPhoneOtp = async (phone: string) => {
  // Тестовый номер для отладки
  if (phone === '+71234567890') {
    return { data: { message: 'Тестовый код отправлен' }, error: null };
  }

  try {
    const { data, error } = await supabase.auth.signInWithOtp({
      phone,
    });
    
    return { data, error };
  } catch (error: any) {
    console.error('Ошибка отправки OTP:', error);
    return { data: null, error };
  }
};

/**
 * Проверка OTP
 * @param phone - Номер телефона
 * @param token - Код подтверждения
 * @returns Результат операции
 */
export const verifyPhoneOtp = async (phone: string, token: string) => {
  // Тестовый номер для отладки
  if (phone === '+71234567890' && token === '123456') {
    return { 
      data: { 
        user: { id: 'test-user-id', phone },
        session: { access_token: 'test-token' } 
      }, 
      error: null 
    };
  }

  try {
    const { data, error } = await supabase.auth.verifyOtp({
      phone,
      token,
      type: 'sms'
    });
    
    // Если верификация успешна, создаем или обновляем запись в таблице couriers
    if (data?.user && !error) {
      console.log('Успешная верификация OTP для телефона:', phone);
      
      // Проверим, существует ли курьер в таблице couriers по телефону
      const { data: courierData, error: courierError } = await supabase
        .from('couriers')
        .select('*')
        .eq('phone', phone)
        .maybeSingle();
      
      // Выводим данные о курьере, если нашли
      if (courierData) {
        console.log('ВЕРИФИКАЦИЯ OTP: НАЙДЕН ID КУРЬЕРА В ТАБЛИЦЕ COURIERS:', courierData.id);
      } else {
        console.log('ВЕРИФИКАЦИЯ OTP: Курьер не найден в таблице couriers');
      }
      
      // Если курьера нет, создадим его
      if (!courierData && !courierError) {
        // Генерируем уникальный ID для курьера
        const courierId = `courier-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
        
        const { data: newCourier, error: createError } = await supabase
          .from('couriers')
          .insert([
            { 
              id: courierId, // Используем сгенерированный ID
              name: data.user.user_metadata?.name || 'Новый пользователь',
              phone: phone,
              status: 'active' as const,
              role: 'courier'
            }
          ])
          .select();
        
        if (createError) {
          console.error('Не удалось создать запись курьера:', createError);
        } else {
          console.log('Создан новый курьер:', newCourier);
          if (newCourier && newCourier.length > 0) {
            console.log('СОЗДАН НОВЫЙ КУРЬЕР С ID В ТАБЛИЦЕ COURIERS:', newCourier[0].id);
          }
        }
      }
    }
    
    return { data, error };
  } catch (error: any) {
    console.error('Ошибка проверки OTP:', error);
    return { data: null, error };
  }
};
